// ─────────────────────────────────────────────────────────────────────────────
// EditNodeModal — "Edit Node" from the row menu: IP address + node name.
//
// Wired to PUT /enterprise-nodes/:id. Both fields go on every request because
// that endpoint takes the same { publicIp, name } pair as create — there is no
// partial update.
//
// publicIp IS editable, and the design was right to show it that way. The note
// that used to live here claimed the backend rejected a changed IP with a 400;
// that is no longer true. Changing it RE-ADDRESSES the node: circuits are torn
// down at the old address and open sessions are closed, so those users must
// reconnect. Identity survives — same node_id, same public key, no new enrollment
// token — so this is not a re-enrollment. Because it costs live sessions, an IP
// edit takes two presses; a rename saves on the first.
//
// Geo is re-detected server-side from the IP and is never sent from here, so the
// stored location cannot drift away from the address.
//
// On success the modal STAYS OPEN to report what happened. Whether the IP
// actually changed, how many sessions that closed, whether the new address
// answered a probe, and the warnings are only stated in this response — closing
// on success would throw the one copy away.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./rowActions.module.css";
import { CloseIcon } from "./icons";
import { dash, nodeLabel } from "../../../format";
import { updateNode } from "../../../../../services/nodesService";

export default function EditNodeModal({ node, onClose, onSaved }) {
  const [form, setForm]   = useState({ ip: node.ip ?? "", name: node.name ?? "" });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [confirmIp, setConfirmIp] = useState(false);   // re-address awaiting 2nd press
  const [result, setResult]       = useState(null);    // success payload

  // Escape closes — but never mid-flight: the request is already on its way and
  // the node would change behind a dismissed modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape" && !saving) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, saving]);

  const label       = dash(nodeLabel(node));
  const trimmedIp   = form.ip.trim();
  const trimmedName = form.name.trim();
  const ipChanging  = trimmedIp !== (node.ip ?? "").trim();
  const dirty       = ipChanging || trimmedName !== (node.name ?? "").trim();
  const complete    = trimmedIp !== "" && trimmedName !== "";

  const submit = async (e) => {
    e.preventDefault();
    if (saving || !complete || !dirty) return;

    // Make re-addressing deliberate. The button then reads "Re-address node" and
    // the second press goes through.
    if (ipChanging && !confirmIp) { setConfirmIp(true); return; }

    setSaving(true);
    setError(null);
    try {
      const res = await updateNode(node.node_id, { publicIp: trimmedIp, name: trimmedName });
      setResult(res);
      // Refresh the table now. An IP change rewrites geo and may have closed
      // sessions, so the row behind this modal is stale the moment we return.
      onSaved?.(res);
    } catch (err) {
      // Already phrased for display; a 400 here names the offending field.
      setError(err.message);
      setConfirmIp(false);
    } finally {
      setSaving(false);
    }
  };

  // ── Success ────────────────────────────────────────────────────────────────
  if (result) {
    // Only report what applies: the IP-change fields are absent on a rename, and
    // printing them as "—" would imply a re-address that never happened.
    const rows = {
      "Node Name":  dash(result.name),
      "IP Address": dash(result.ip),
      ...(result.ip_changed ? { "Previous IP": dash(result.previous_ip) } : {}),
      "Region":     dash(result.geo?.region ?? result.region),
      ...(result.ip_changed && result.reachable !== null
        ? { "New address": result.reachable ? "Reachable" : "Not answering" }
        : {}),
      ...(result.ip_changed && Number.isFinite(result.sessions_closed)
        ? { "Sessions closed": String(result.sessions_closed) }
        : {}),
    };

    return createPortal(
      <div className={styles.overlay} onClick={onClose}>
        <div
          className={styles.modal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-node-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>

          <h2 className={styles.title} id="edit-node-title">
            {result.ip_changed ? "Node re-addressed" : "Node updated"}
          </h2>
          <p className={styles.subtitle}>
            {result.ip_changed
              ? "The node must re-establish its heartbeat from the new address before it routes traffic again."
              : "Changes saved."}
          </p>

          <dl className={styles.summary}>
            {Object.entries(rows).map(([key, value]) => (
              <div key={key} className={styles.row}>
                <dt className={styles.key}>{key}</dt>
                <dd className={styles.value}>{value}</dd>
              </div>
            ))}
          </dl>

          {result.warnings.map((w) => (
            <p key={w} className={styles.warnText}>{w}</p>
          ))}

          <div className={styles.footer}>
            <button type="button" className={styles.btnPrimary} onClick={onClose}>Done</button>
          </div>
        </div>
      </div>,
      document.body,
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return createPortal(
    <div className={styles.overlay} onClick={saving ? undefined : onClose}>
      <form
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-node-title"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
      >
        <button
          type="button"
          className={styles.close}
          onClick={onClose}
          disabled={saving}
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h2 className={styles.title} id="edit-node-title">Edit Node</h2>
        <p className={styles.subtitle}>
          Editing <span className={styles.nodeName}>{label}</span>
        </p>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-node-ip">IP Address</label>
          <input
            id="edit-node-ip"
            className={styles.input}
            autoComplete="off"
            spellCheck={false}
            disabled={saving}
            value={form.ip}
            onChange={(e) => {
              setForm((f) => ({ ...f, ip: e.target.value }));
              setConfirmIp(false);   // edited again — confirm afresh
            }}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.label} htmlFor="edit-node-name">Node Name</label>
          <input
            id="edit-node-name"
            className={styles.input}
            autoComplete="off"
            disabled={saving}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
        </div>

        {/* Shown only for a real re-address. A rename costs nothing, so it says
            nothing. */}
        {ipChanging && (
          <p className={styles.warnText}>
            Changing the IP re-addresses this node. Its circuits are torn down at{" "}
            {dash(node.ip)} and any open sessions are closed — those users must
            reconnect. The node keeps its identity and enrollment.
          </p>
        )}

        {error && <p className={styles.warnText} role="alert">{error}</p>}

        <div className={styles.footer}>
          <button type="button" className={styles.btnGhost} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="submit"
            className={ipChanging && confirmIp ? styles.btnDanger : styles.btnPrimary}
            disabled={saving || !complete || !dirty}
          >
            {saving
              ? "Saving…"
              : ipChanging && confirmIp
                ? "Re-address node"
                : "Save Changes"}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
